import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Realtime } from 'magicbell-js/realtime';
import {
    type Notification as MagicBellNotification,
    Client as MagicBellUserClient,
} from 'magicbell-js/user-client';
import * as React from 'react';

import { apiClient } from '../../state';
import {
    getMagicBellNotificationsQueryKey,
    getMagicBellTokenQueryKey,
    getMagicBellUnreadCountQueryKey,
} from './queryKeys';

const MAGICBELL_TOKEN_REFRESH_BUFFER_MS = 60 * 60 * 1000;
const MAGICBELL_TOKEN_ERROR_RETRY_MS = 5 * 60 * 1000;

type MagicBellAvailability = {
    status: 'loading' | 'ready' | 'disabled';
};

type MagicBellEventListener = (notification: MagicBellNotification) => void;
type MagicBellToken = {
    value: string;
    expiresAt: number | null;
};

type MagicBellContextValue = MagicBellAvailability & {
    client: MagicBellUserClient | null;
    subscribe: (listener: MagicBellEventListener) => () => void;
};

const MagicBellContext = React.createContext<MagicBellContextValue | null>(null);

/**
 * Provides the authenticated MagicBell client context to notification UI descendants.
 */
export function MagicBellProvider(props: {
    children: React.ReactNode;
    userId: string;
}): React.ReactElement {
    const { children, userId } = props;
    const queryClient = useQueryClient();
    const listenersRef = React.useRef(new Set<MagicBellEventListener>());
    const tokenQuery = useQuery({
        queryKey: getMagicBellTokenQueryKey(userId),
        queryFn: async () => {
            const { data } = await apiClient.users.createUserNotificationsToken();
            return {
                value: data.token,
                expiresAt: decodeTokenExpiration(data.token),
            } satisfies MagicBellToken;
        },
        retry: 3,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30_000),
        refetchInterval: (query) =>
            getTokenRefreshInterval(query.state.data, query.state.status === 'error'),
        refetchIntervalInBackground: true,
        refetchOnReconnect: 'always',
        refetchOnWindowFocus: (query) =>
            isTokenDueForRefresh(query.state.data) ? 'always' : false,
    });
    const token = tokenQuery.data?.value ?? null;
    const client = React.useMemo(
        () => (token ? new MagicBellUserClient({ token }) : null),
        [token]
    );
    const status: MagicBellAvailability['status'] = tokenQuery.isPending
        ? 'loading'
        : token
          ? 'ready'
          : 'disabled';

    React.useEffect(() => {
        if (!client) {
            return;
        }

        let isMounted = true;
        const realtime = new Realtime(client);

        realtime
            .listen((notification) => {
                if (!isMounted) {
                    return;
                }

                void Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: getMagicBellNotificationsQueryKey(userId),
                    }),
                    queryClient.invalidateQueries({
                        queryKey: getMagicBellUnreadCountQueryKey(userId),
                    }),
                ]);
                listenersRef.current.forEach((listener) => {
                    listener(notification);
                });
            })
            .catch(() => undefined);

        return () => {
            isMounted = false;
            realtime.disconnect();
        };
    }, [client, queryClient, userId]);

    const value = React.useMemo<MagicBellContextValue>(
        () => ({
            status,
            client,
            subscribe: (listener) => {
                listenersRef.current.add(listener);

                return () => {
                    listenersRef.current.delete(listener);
                };
            },
        }),
        [client, status]
    );

    return <MagicBellContext.Provider value={value}>{children}</MagicBellContext.Provider>;
}

/**
 * Returns whether MagicBell UI can be rendered for the current user.
 */
export function useMagicBellAvailability(): MagicBellAvailability {
    const { status } = useMagicBellContext();

    return { status };
}

/**
 * Returns the authenticated MagicBell client.
 */
export function useMagicBellClient(): MagicBellUserClient | null {
    return useMagicBellContext().client;
}

/**
 * Subscribes to raw realtime notifications emitted for the current user.
 */
export function useMagicBellSubscription(): MagicBellContextValue['subscribe'] {
    return useMagicBellContext().subscribe;
}

function useMagicBellContext(): MagicBellContextValue {
    const context = React.useContext(MagicBellContext);

    if (!context) {
        throw new Error('MagicBellProvider is missing');
    }

    return context;
}

function getTokenRefreshInterval(
    token: MagicBellToken | undefined,
    hasError: boolean
): number | false {
    if (!token?.expiresAt) {
        return hasError ? MAGICBELL_TOKEN_ERROR_RETRY_MS : false;
    }

    return Math.max(token.expiresAt - Date.now() - MAGICBELL_TOKEN_REFRESH_BUFFER_MS, 60_000);
}

/**
 * Whether the token is within the refresh window, e.g. because a background tab delayed the refresh.
 */
function isTokenDueForRefresh(token: MagicBellToken | undefined): boolean {
    if (!token?.expiresAt) {
        return true;
    }

    return token.expiresAt - Date.now() <= MAGICBELL_TOKEN_REFRESH_BUFFER_MS;
}

function decodeTokenExpiration(token: string): number | null {
    const payload = token.split('.')[1];

    if (!payload) {
        return null;
    }

    try {
        const normalizedPayload = payload.replace(/-/g, '+').replace(/_/g, '/');
        const paddedPayload = normalizedPayload.padEnd(
            Math.ceil(normalizedPayload.length / 4) * 4,
            '='
        );
        const decoded = JSON.parse(globalThis.atob(paddedPayload)) as { exp?: number };

        return typeof decoded.exp === 'number' ? decoded.exp * 1000 : null;
    } catch {
        return null;
    }
}
